import { useCallback, useEffect, useState } from "react";
import { api, notifyError } from "@/lib/api";
import { CalendarCheck, FloppyDisk, UploadSimple, ArrowsClockwise } from "@phosphor-icons/react";

// Self-contained onboarding for companies migrating in: set a compliance start
// date (so old periods aren't flagged red), record the last filed return, change
// the CRO Annual Return Date, and bulk-mark past CRO returns as filed via CSV/manual.
export default function MigrationPanel({ onCroChange }) {
  const [co, setCo] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [lastReturn, setLastReturn] = useState("");
  const [croArd, setCroArd] = useState("");
  const [savingBaseline, setSavingBaseline] = useState(false);
  const [savingArd, setSavingArd] = useState(false);
  const [importing, setImporting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/compliance/company");
      setCo(r.data);
      setStartDate(r.data.compliance_start_date || "");
      setLastReturn(r.data.last_annual_return_date || "");
      setCroArd(r.data.cro_ard || "");
    } catch (e) { notifyError(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveBaseline = async () => {
    if (!co) return;
    setSavingBaseline(true); setMsg(null);
    try {
      await api.put("/compliance/company", { ...co, compliance_start_date: startDate, last_annual_return_date: lastReturn });
      setMsg({ type: "ok", text: startDate ? `Fresh start set — nothing before ${startDate} will be flagged as overdue.` : "Migration baseline cleared." });
      load(); onCroChange && onCroChange();
    } catch (e) { notifyError(e); } finally { setSavingBaseline(false); }
  };

  const saveArd = async () => {
    setSavingArd(true); setMsg(null);
    try {
      await api.post("/compliance/cro/ard", { cro_ard: croArd });
      setMsg({ type: "ok", text: croArd ? `CRO Annual Return Date updated to ${croArd}.` : "CRO date override cleared — back to the incorporation-based schedule." });
      load(); onCroChange && onCroChange();
    } catch (e) { notifyError(e); } finally { setSavingArd(false); }
  };

  const onCSV = async (file) => {
    if (!file) return;
    setImporting(true); setMsg(null);
    try {
      const text = await file.text();
      const dates = [];
      for (const line of text.split(/\r?\n/)) {
        for (const cell of line.split(/[,;\t]/)) {
          const m = cell.trim().match(/\d{4}-\d{2}-\d{2}/);
          if (m && !dates.includes(m[0])) dates.push(m[0]);
        }
      }
      if (!dates.length) { setMsg({ type: "err", text: "No YYYY-MM-DD dates found in that file." }); return; }
      await api.post("/compliance/cro/acknowledge-bulk", { dates });
      setMsg({ type: "ok", text: `Marked ${dates.length} past CRO return date${dates.length === 1 ? "" : "s"} as filed.` });
      load(); onCroChange && onCroChange();
    } catch (e) { notifyError(e); } finally { setImporting(false); }
  };

  return (
    <div className="glass rounded-xl p-6 space-y-6" data-testid="migration-panel">
      <div>
        <div className="flex items-center gap-2 eyebrow mb-1"><CalendarCheck size={15} weight="fill" style={{ color: "#FFB020" }} /> Moving in / already trading</div>
        <h3 className="font-display text-2xl">Start fresh — don't flag old deadlines</h3>
        <p className="text-white/55 text-sm mt-2 max-w-2xl">If you've been trading for a while, set the date you want PerCen360 to start tracking from. Anything filed before it is treated as done, so you won't see red flags for historical returns. You can still add your CRO history below.</p>
      </div>

      {msg && (
        <div data-testid="migration-msg" className={`text-sm rounded-lg px-3 py-2 ${msg.type === "ok" ? "bg-[#00A86B]/15 text-[#4ade80]" : "bg-[#E63946]/15 text-[#fca5a5]"}`}>{msg.text}</div>
      )}

      {/* Baseline */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
        <div>
          <label>Compliance start date</label>
          <input type="date" data-testid="input-compliance-start" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <div className="text-[11px] text-white/40 mt-1">Deadlines before this date won't be flagged.</div>
        </div>
        <div>
          <label>Last Annual Return filed (optional)</label>
          <input type="date" data-testid="input-last-return" value={lastReturn} onChange={(e) => setLastReturn(e.target.value)} />
          <div className="text-[11px] text-white/40 mt-1">For your records.</div>
        </div>
      </div>
      <div className="flex justify-end">
        <button data-testid="btn-save-baseline" onClick={saveBaseline} disabled={savingBaseline} className="btn-gold">
          <FloppyDisk size={16} weight="fill" /> {savingBaseline ? "Saving…" : "Save fresh-start date"}
        </button>
      </div>

      <div className="h-px bg-white/10" />

      {/* CRO ARD change */}
      <div>
        <h4 className="font-display text-lg mb-1">Change CRO Annual Return Date</h4>
        <p className="text-white/55 text-sm mb-3 max-w-2xl">Filed a B73 or your ARD isn't 6 months after incorporation? Set your actual next Annual Return Date and we'll re-anchor the whole schedule (filing deadline = ARD + 56 days).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <div>
            <label>Annual Return Date (ARD)</label>
            <input type="date" data-testid="input-cro-ard" value={croArd} onChange={(e) => setCroArd(e.target.value)} />
          </div>
          <div className="flex justify-end">
            <button data-testid="btn-save-cro-ard" onClick={saveArd} disabled={savingArd} className="btn-gold">
              <ArrowsClockwise size={16} weight="fill" /> {savingArd ? "Updating…" : "Update ARD"}
            </button>
          </div>
        </div>
      </div>

      <div className="h-px bg-white/10" />

      {/* CRO history CSV */}
      <div>
        <h4 className="font-display text-lg mb-1">Import past CRO returns (CSV)</h4>
        <p className="text-white/55 text-sm mb-3 max-w-2xl">Upload a CSV containing the filing dates of returns you've already submitted — we detect any YYYY-MM-DD dates and mark those returns as filed so they aren't flagged.</p>
        <label className="btn-ghost cursor-pointer inline-flex" data-testid="btn-cro-csv">
          <UploadSimple size={16} weight="fill" /> {importing ? "Importing…" : "Upload CRO history CSV"}
          <input type="file" accept=".csv,text/csv" hidden onChange={(e) => onCSV(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
}
